import type { CSSProperties } from 'react';

/*
 * The player's art in verde and gold for every track (the handoff's
 * revision: one treatment, no hue per category), converted from oklch for the
 * WebView floor.
 */
const HALO =
  'conic-gradient(from 0deg, rgba(95,180,121,0) 0%, rgba(95,180,121,0.35) 25%, rgba(95,180,121,0) 50%, rgba(203,169,96,0.3) 75%, rgba(95,180,121,0) 100%)';
const ORB =
  'radial-gradient(circle at 35% 30%, rgba(140,209,158,0.9), rgba(26,118,63,0.9) 50%, rgba(0,57,24,0.7) 100%)';
const RINGS = [
  { size: 240, delay: 0, alpha: 0.28, width: 1 },
  { size: 200, delay: 0.8, alpha: 0.4, width: 1 },
  { size: 162, delay: 1.6, alpha: 0.55, width: 1.5 },
] as const;

/**
 * A slow halo, three breathing rings and an orb behind the track's frequency.
 * Everything moves only while the track plays, and not at all with reduced
 * motion (`mp-motion`).
 */
export function FrequencyArt({
  playing,
  label,
  sub,
}: {
  playing: boolean;
  label: string | null;
  sub: string | null;
}) {
  const run: CSSProperties = { animationPlayState: playing ? 'running' : 'paused' };
  return (
    <div className="relative mx-auto mt-4 flex h-[250px] w-[280px] max-w-full items-center justify-center">
      <div aria-hidden className="absolute inset-0 flex items-center justify-center">
        <div
          className="mp-motion absolute h-[250px] w-[250px] rounded-full opacity-90 blur-[14px]"
          style={{ background: HALO, animation: 'mp-spin 24s linear infinite', ...run }}
        />
        {RINGS.map((r) => (
          <div
            key={r.size}
            className="mp-motion absolute rounded-full"
            style={{
              width: r.size,
              height: r.size,
              border: `${r.width}px solid rgba(133,202,152,${r.alpha})`,
              boxShadow: `0 0 ${r.size / 6}px rgba(95,180,121,${r.alpha / 2})`,
              animation: `mp-pulse ${6 + r.size / 60}s ease-in-out ${r.delay}s infinite`,
              ...run,
            }}
          />
        ))}
        <div
          className="mp-motion absolute h-[130px] w-[130px] rounded-full"
          style={{
            background: ORB,
            boxShadow: '0 0 70px 10px rgba(53,150,88,0.35)',
            animation: 'mp-pulse 7s ease-in-out infinite',
            ...run,
          }}
        />
      </div>
      {label && (
        <div className="pointer-events-none relative text-center">
          <p
            className="font-mono text-[40px] font-medium leading-none tracking-[-0.02em]"
            style={{ textShadow: '0 1px 16px rgba(0,0,0,0.5)' }}
          >
            {label}
          </p>
          {sub && (
            <p className="mt-1.5 font-mono text-[0.64rem] uppercase tracking-[0.16em] text-paper/70">
              {sub}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
